/**
 * <td-reviews-carousel-okendo>
 * Buy box reviews carousel. Fetches reviews from the Okendo API and renders them
 * into a Swiper (loaded by snippets/td-swiper-assets.liquid).
 * Markup: snippets/td-reviews-carousel-okendo.liquid
 * Styles: assets/td-reviews-carousel-okendo.css
 */
if (!customElements.get('td-reviews-carousel-okendo')) {
  // English fallbacks when the snippet's data-strings (locale keys) are missing.
  const STRING_FALLBACKS = {
    verified: 'Verified Buyer',
    anonymous: 'Customer',
    today: 'Today',
    dayAgo: '1 day ago',
    daysAgo: '[count] days ago',
    monthAgo: '1 month ago',
    monthsAgo: '[count] months ago',
    yearAgo: '1 year ago',
    yearsAgo: '[count] years ago',
  };

  const SELECTORS = {
    root: '[data-td-reviews-carousel]',
    nav: '[data-td-reviews-nav]',
    navNext: '.td-reviews-carousel-okendo__nav-btn--next',
    navPrev: '.td-reviews-carousel-okendo__nav-btn--prev'
  };

  /**
   * Custom element that loads Okendo reviews and mounts them in a Swiper carousel.
   * @extends HTMLElement
   */
  class TdReviewsCarouselOkendo extends HTMLElement {
    constructor() {
      super();
      this.productId = this.dataset.productId || '';
      this.okendoUserId = this.dataset.okendoUserId || '';
      this.settings = {};
      try {
        this.settings = JSON.parse(this.dataset.settings || '{}');
      } catch (e) {
        console.warn('TdReviewsCarouselOkendo: Invalid settings', e);
      }
      this.limit = Math.min(10, Math.max(3, this.settings.limit || 5));
      this.qualityFilter = this.settings.qualityFilter || 'balanced';
      this.swiper = null;
      this.abortController = null;
      this.viewAllCta = null;
    }

    /**
     * Starts loading reviews when the element is added to the page.
     * @returns {void}
     */
    connectedCallback() {
      if (!this.productId || !this.okendoUserId) {
        this.showState('error');
        return;
      }
      this.abortController = new AbortController();
      this.attachViewAllListener();
      this.fetchReviews();
    }

    /**
     * Removes listeners and destroys the Swiper (theme editor re-renders).
     * @returns {void}
     */
    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      if (this.viewAllCta) {
        delete this.viewAllCta.dataset.tdBound;
        this.viewAllCta = null;
      }
      if (this.swiper && typeof this.swiper.destroy === 'function') {
        this.swiper.destroy(true, true);
      }
      this.swiper = null;
    }

    /**
     * Attaches click listener to "View all reviews" link for smooth scroll to reviews section.
     * @returns {void}
     */
    attachViewAllListener() {
      const wrapper = this.closest(SELECTORS.root);
      const cta = wrapper?.querySelector('[data-view-all]');
      if (!cta || cta.dataset.tdBound) return;
      cta.dataset.tdBound = 'true';
      this.viewAllCta = cta;
      cta.addEventListener(
        'click',
        (e) => {
          // Target comes from the link's own href, so the snippet owns the anchor.
          const hash = cta.hash;
          const el = hash && hash.length > 1 ? document.getElementById(hash.slice(1)) : null;
          if (el) {
            e.preventDefault();
            el.scrollIntoView({ behavior: 'smooth' });
          }
        },
        { signal: this.abortController?.signal }
      );
    }

    /**
     * Returns Okendo API orderBy param based on quality filter.
     * Balanced/trust_focused: helpful desc; all: date desc.
     * @returns {string}
     */
    getOrderBy() {
      const map = {
        balanced: 'helpful%20desc',
        trust_focused: 'helpful%20desc',
        all: 'date%20desc'
      };
      return map[this.qualityFilter] || map.balanced;
    }

    /**
     * Applies client-side quality filter to reviews array.
     * Returns filtered array (order preserved from API).
     * @param {Array<Object>} reviews - Reviews from the Okendo API.
     * @returns {Array<Object>}
     */
    filterReviews(reviews) {
      if (!Array.isArray(reviews) || reviews.length === 0) return [];
      const f = this.qualityFilter;
      if (f === 'all') return reviews;
      return reviews.filter((r) => {
        const verified = !!r.reviewer?.isVerified;
        const rating = r.rating || 0;
        const incentivized = !!r.isIncentivized;
        if (f === 'balanced') return verified && rating >= 4 && !incentivized;
        if (f === 'trust_focused') return verified && rating >= 4;
        return true;
      });
    }

    /**
     * @param {string} state - loading | carousel | empty | error
     * @returns {void}
     */
    showState(state) {
      this.dataset.state = state;
      const errorEl = this.querySelector('[data-error]');
      if (errorEl) errorEl.textContent = state === 'error' ? errorEl.dataset.errorText || '' : '';
    }

    /**
     * Formats a date string as "N MONTHS AGO" etc.
     * @param {string} dateStr
     * @returns {string}
     */
    formatDateAgo(dateStr) {
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return '';
      const days = Math.floor((Date.now() - date.getTime()) / 86400000);
      const years = Math.floor(days / 365);
      const months = Math.floor(days / 30);
      if (years >= 1) return years === 1 ? this.t('yearAgo') : this.t('yearsAgo', years);
      if (months >= 1) return months === 1 ? this.t('monthAgo') : this.t('monthsAgo', months);
      if (days >= 1) return days === 1 ? this.t('dayAgo') : this.t('daysAgo', days);
      return this.t('today');
    }

    /**
     * Returns a translated string from data-strings, with [count] filled in.
     * @param {string} key
     * @param {number} [count]
     * @returns {string}
     */
    t(key, count) {
      if (!this.strings) {
        try {
          this.strings = JSON.parse(this.dataset.strings || '{}');
        } catch {
          this.strings = {};
        }
      }
      const value = this.strings[key] || STRING_FALLBACKS[key] || '';
      return count === undefined ? value : value.replace('[count]', String(count));
    }

    /**
     * Escapes HTML characters in a string.
     * @param {string} str
     * @returns {string}
     */
    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    /**
     * Truncates text to a maximum number of words, appending an ellipsis if truncated.
     * Runs on the raw string, before escaping, so an entity is never cut in half.
     * @param {string} str
     * @param {number} maxWords
     * @returns {string}
     */
    truncateWords(str, maxWords) {
      if (!str) return '';
      const words = str.trim().split(/\s+/);
      if (words.length <= maxWords) return str;
      return words.slice(0, maxWords).join(' ') + '…';
    }

    /**
     * Inline SVG star row. The filled layer is clipped to the rating percentage.
     * @param {number} rating - 0 to 5
     * @returns {string}
     */
    starsHtml(rating) {
      const star =
        '<svg viewBox="0 0 20 19" width="16" height="15" aria-hidden="true" focusable="false"><path d="M10 0l2.94 6.06 6.66.92-4.85 4.66 1.18 6.62L10 15.1l-5.93 3.16 1.18-6.62L.4 6.98l6.66-.92L10 0z" fill="currentColor"/></svg>';
      const row = star.repeat(5);
      const percent = Math.max(0, Math.min(100, (rating / 5) * 100));
      // Translated label from the snippet (accessibility.star_reviews_info).
      const template = this.dataset.starsLabel || '[rating] out of 5 stars';
      const label = this.escapeHtml(template.replace('[rating]', String(rating))).replace(/"/g, '&quot;');
      return `
        <span class="td-reviews-carousel-okendo__stars" role="img" aria-label="${label}">
          <span class="td-reviews-carousel-okendo__stars-base">${row}</span>
          <span class="td-reviews-carousel-okendo__stars-fill" style="--td-reviews-rating: ${percent}%">${row}</span>
        </span>
      `;
    }

    /**
     * Fetches reviews from the Okendo API and renders them, or shows the empty/error state.
     * @returns {Promise<void>}
     */
    async fetchReviews() {
      const orderBy = this.getOrderBy();
      const url = `https://api.okendo.io/v1/stores/${encodeURIComponent(this.okendoUserId)}/products/shopify-${encodeURIComponent(this.productId)}/reviews?limit=25&orderBy=${orderBy}`;
      try {
        const res = await fetch(url, { signal: this.abortController?.signal });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!this.isConnected) return;
        const raw = data?.reviews || [];
        const reviews = this.filterReviews(raw).slice(0, this.limit);
        if (reviews.length === 0) {
          this.showState('empty');
          return;
        }
        this.renderCarousel(reviews);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.warn('TdReviewsCarouselOkendo: Failed to fetch reviews', err);
        this.showState('error');
      }
    }

    /**
     * @param {Array<Object>} reviews
     * @returns {void}
     */
    renderCarousel(reviews) {
      const carouselEl = this.querySelector('[data-carousel]');
      if (!carouselEl) return;

      // Swiper's loop mode needs more slides than are visible (2, counting the
      // peeking card); with fewer, rewind gives the arrows the same wrap-around feel.
      // slidesPerView/spaceBetween are mirrored by --td-reviews-per-view/-gap in the CSS.
      const canLoop = reviews.length >= 4;
      const opts = {
        loop: canLoop,
        rewind: !canLoop,
        slidesPerView: 1,
        spaceBetween: 24,
        watchOverflow: true,
        a11y: { enabled: true },
        breakpoints: {
          769: { slidesPerView: 1.35, spaceBetween: 40 }
        }
      };
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.settings.autoscroll && !reduceMotion) {
        opts.autoplay = {
          delay: (this.settings.autoscrollSpeed || 5) * 1000,
          pauseOnMouseEnter: true,
          disableOnInteraction: true
        };
      }
      const containerId = this.dataset.containerId || '';
      const wrapper = containerId
        ? document.getElementById(containerId)
        : this.closest(SELECTORS.root);
      const navWrap = wrapper?.querySelector(SELECTORS.nav);
      // Elements, not selector strings, so Swiper never binds another carousel's arrows.
      const nextEl = navWrap?.querySelector(SELECTORS.navNext);
      const prevEl = navWrap?.querySelector(SELECTORS.navPrev);
      if (this.settings.showNav && nextEl && prevEl) {
        opts.navigation = { nextEl, prevEl };
      }

      const slidesHtml = reviews
        .map((r) => {
          const rating = Math.max(0, Math.min(5, Number(r.rating) || 0));
          const title = this.escapeHtml(r.title || '');
          const body = this.escapeHtml(this.truncateWords(r.body || '', 40));
          const name = this.escapeHtml(r.reviewer?.displayName || this.t('anonymous'));
          const verified = !!r.reviewer?.isVerified;
          const dateStr = this.escapeHtml(this.formatDateAgo(r.dateCreated || ''));
          return `
            <div class="swiper-slide td-reviews-carousel-okendo__slide" data-swiper-slide>
              <article class="td-reviews-carousel-okendo__card">
                <div class="td-reviews-carousel-okendo__card-top">
                  ${this.starsHtml(rating)}
                  ${dateStr ? `<span class="td-reviews-carousel-okendo__date">${dateStr}</span>` : ''}
                </div>
                ${title ? `<p class="td-reviews-carousel-okendo__title">${title}</p>` : ''}
                ${body ? `<p class="td-reviews-carousel-okendo__body">${body}</p>` : ''}
                <p class="td-reviews-carousel-okendo__reviewer-container">
                  <span class="td-reviews-carousel-okendo__reviewer">${name}</span>
                  ${verified ? `<span class="td-reviews-carousel-okendo__verified">${this.escapeHtml(this.t('verified'))}</span>` : ''}
                </p>
              </article>
            </div>
          `;
        })
        .join('');

      carouselEl.innerHTML = `
        <div class="swiper td-reviews-carousel-okendo__swiper" data-swiper>
          <div class="swiper-wrapper td-swiper-wrapper">
            ${slidesHtml}
          </div>
        </div>
      `;

      this.showState('carousel');

      if (navWrap && this.settings.showNav && reviews.length > 1) {
        navWrap.classList.remove('td-reviews-carousel-okendo__nav--hidden');
      }

      this.initSwiper(carouselEl, opts);
      if (opts.autoplay) this.pauseAutoplayOnFocus(wrapper || this);
    }

    /**
     * Pauses autoplay while keyboard focus is inside the block (arrows, slides, CTA).
     * @param {Element} root - The block wrapper.
     * @returns {void}
     */
    pauseAutoplayOnFocus(root) {
      const signal = this.abortController?.signal;
      root.addEventListener('focusin', () => this.swiper?.autoplay?.pause(), { signal });
      root.addEventListener(
        'focusout',
        (e) => {
          if (!root.contains(e.relatedTarget)) this.swiper?.autoplay?.resume();
        },
        { signal }
      );
    }

    /**
     * @param {Element} container
     * @param {Object} opts - Swiper options
     * @returns {void}
     */
    initSwiper(container, opts) {
      const swiperEl = container.querySelector('[data-swiper]');
      if (!swiperEl) return;

      /**
       * Creates the Swiper now, or once swiper-bundle.min.js has loaded.
       * @returns {void}
       */
      const init = () => {
        if (this.swiper || !this.isConnected) return;
        if (window.Swiper) {
          this.swiper = new window.Swiper(swiperEl, opts);
          return;
        }
        // td-swiper-slider.js injects swiper-bundle.min.js asynchronously, so it can
        // still be in flight after window load. Wait for that script, then retry once.
        const script = document.querySelector('script[src*="swiper-bundle"]');
        if (script) {
          script.addEventListener(
            'load',
            () => {
              if (window.Swiper && !this.swiper && this.isConnected) {
                this.swiper = new window.Swiper(swiperEl, opts);
              }
            },
            { once: true }
          );
        }
      };

      if (document.readyState === 'complete') {
        init();
      } else {
        window.addEventListener('load', init, { once: true });
      }
    }
  }

  customElements.define('td-reviews-carousel-okendo', TdReviewsCarouselOkendo);
}
